import { jwtDecode } from "jwt-decode";
import { Navigate } from "react-router";

export const Label = ({ title, required }) => (
    <label 
	className=" block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">
      {title} {required && <span className="text-red-500">*</span>}
    </label>
);
  

function normalizeResponse(response) {
	if (!response) {
		throw new Error("Invalid response object");
	}

	if (response.result) {
		return {
			responseDescription: response.result.responseDescription,
			responseCode: response.result.responseCode,
		};
	}

	return {
		responseDescription: response.responseDescription,
		responseCode: response.responseCode,
	};
}

/**
 * Handles API calls with customizable options, optional system token, and request cancellation support.
 *
 * @param {string} url - The API endpoint URL.
 * @param {Object} options - The options for the request (method, headers, body, etc.).
 * @param {boolean} [systemToken=false] - Flag to include system token in the request headers.
 * @param {AbortSignal} [signal=null] - Optional abort signal to cancel the request.
 * @returns {Object|null} - The response data or null in case of failure or cancellation.
 */

export const handleApiCall = async (url, options = {}, signal = null) => {
  const { method = "GET", headers = {}, body = null } = options;

  const controller = new AbortController();
  const currentSignal = signal || controller.signal;

  try {
    const response = await fetch(url, {
      method,
      headers: {
        "Content-Type": "application/json",
        "X-JWT-Assertion": localStorage.getItem("token"),
        ...headers,
      },
      body: body ? JSON.stringify(body) : null,
      signal: currentSignal,
    });

    if (response.status === 401) {
      localStorage.removeItem("token");
      window.location.replace("/login");
      return null;
    }

    if (response.status === 403) {
      return {
        responseDescription: "You are not authorized to perform this action.",
        responseCode: 403,
        responseStatus: false,
      };
    }

    const data = await response.json();

    if (response.status === 200) {
      // ✅ handle both wrapped and raw
      if (data?.result?.responseCode === 200) {
        return { ...data, responseStatus: true, status: 200 };
      }

      if (Array.isArray(data) || typeof data === "object") {
        return { data, responseStatus: true, status: 200 };
      }
    }

    // Normalize error response
    const data1 = normalizeResponse(data);
    return { ...data1, responseStatus: false, status: response.status };

  } catch (error) {
    if (error.name === "AbortError") {
      return {
        responseDescription: "API request was aborted",
        responseCode: 299,
        responseStatus: false,
      };
    }
    console.error("API call failed:", error);
    return {
      responseDescription: "Something went wrong, please try again later.",
      responseCode: 500,
      responseStatus: false,
    };
  }
};

// export const handleApiCall = async (
// 	url,
// 	options = {},
// 	signal = null
// ) => {
// 	const { method = "GET", headers = {}, body = null } = options;

// 	const controller = new AbortController();
// 	const currentSignal = signal || controller.signal;

// 	try {
// 		const response = await fetch(url, {
// 			method,
// 			headers: {
// 				"Content-Type": "application/json",
// 				"X-JWT-Assertion":localStorage.getItem("token"),
// 				...headers,
// 			},
// 			body: body ? JSON.stringify(body) : null,
// 			signal: currentSignal, // Attach the signal to the request
// 		});
// 		if (response.status === 401) {
// 			if (localStorage.removeItem("token") === undefined) {
// 				window.location.replace("/login");
// 			}
// 			return null;
// 		}
// 		if (response.status === 403) {
// 			return {
// 				responseDescription:
// 					"You are not authorized to perform this action.",
// 				responseCode: 403,
// 				responseStatus: false,
// 			};
// 		}

// 		const data = await response.json();

// 		if (response.status === 200) {
// 			if (data.result.responseCode === 200) {
// 				return { ...data, responseStatus: true };
// 			}
// 		}

// 		const data1 = normalizeResponse(data);

// 		return { ...data1, responseStatus: false };
// 	} catch (error) {
// 		if (error.name === "AbortError") {
// 			return {
// 				responseDescription: "API request was aborted",
// 				responseCode: 299,
// 				responseStatus: false,
// 			};
// 		} else {
// 			console.error("API call failed:", error);
// 			return {
// 				responseDescription:
// 					"Something went wrong, please try again later.",
// 				responseCode: 500,
// 				responseStatus: false,
// 			};
// 		}
// 	}
// };

// formatDate.js
export const formatDate = (dateInput, format = 'MMM dd, yyyy') => {
	if (!dateInput) return '';
  
	const date = new Date(dateInput);
  
	const optionsMap = {
	  'MMM dd, yyyy': { year: 'numeric', month: 'short', day: '2-digit' },
	  'MM/dd/yyyy': { year: 'numeric', month: '2-digit', day: '2-digit' },
	  'yyyy-MM-dd': { year: 'numeric', month: '2-digit', day: '2-digit' },
	  'full': { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' },
	  'time': { hour: '2-digit', minute: '2-digit', hour12: true },
	};
  
	const options = optionsMap[format] || optionsMap['MMM dd, yyyy'];
  
	return new Intl.DateTimeFormat('en-US', options).format(date);
  };
  

  export const formatAmount = (value) => {
	if (isNaN(value)) return '0.00';
	return new Intl.NumberFormat('en-US', {
	  minimumFractionDigits: 2,
	  maximumFractionDigits: 2,
	}).format(Number(value));
  };
  
export const jsonToQueryParams = (json) => {
	const queryParams = Object.keys(json)
		.filter((key) => json[key] !== null && json[key] !== "")
		.map((key) => `${encodeURIComponent(key)}=${encodeURIComponent(json[key])}`)
		.join("&");

	return queryParams;
};
export const sleep = (m) => new Promise((r) => setTimeout(r, m));


export const ProtectedRoute = ({ allowedRoles, children }) => {
  const token = localStorage.getItem("token");
  let decoded = null;

  try {
    decoded = token ? jwtDecode(token) : null;
  } catch (error) {
    console.error("Invalid token", error);
    localStorage.removeItem("token"); // clear invalid token too
    return <Navigate to="/login" replace />;
  }

  //  Expiry check (this must run before role validation)
  const currentTime = Math.floor(Date.now() / 1000); // in seconds
  if (!decoded || (decoded?.exp && decoded.exp < currentTime)) {
    localStorage.removeItem("token");
    return <Navigate to="/login" replace />;
  }

  const userRoles = decoded?.authorities || [];
  const hasAccess = userRoles.some((role) => allowedRoles.includes(role));

  return hasAccess ? children : <Navigate to="/unauthorized" replace />;
};

/**
 * Convert DOB (YYYY-MM-DD) to age
 */
export const dobToAge = (dob) => {
  if (!dob) return null;

  const birthDate = new Date(dob);
  if (isNaN(birthDate)) return null;

  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();

  const monthDiff = today.getMonth() - birthDate.getMonth();
  const dayDiff = today.getDate() - birthDate.getDate();

  // Birthday not reached yet this year
  if (monthDiff < 0 || (monthDiff === 0 && dayDiff < 0)) {
    age--;
  }

  return age;
};

export const formatHeightUnit = (unit) => {
  switch (unit) {
    case "FT_IN":
      return "ft/in";
    case "CM":
      return "CM";
    case "M":
      return "M";
    default:
      return unit;
  }
};